import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { GeneralService, GeneralLanguage } from '@gs-2.0/core';
import { CharityFormComponent } from '../charity-form/charity-form.component';
import { ProjectFormComponent } from '../project-form/project-form.component';

export type EntityType = 'charity' | 'project';

export interface LanguageSelectionDialogData {
  languages?: GeneralLanguage[];
  title?: string;
  isEditMode?: boolean;
  entityType?: EntityType;
  /**
   * Forwarded to the project form (add mode) so a charity admin may pick the
   * charity per project. See ProjectFormData.allowCharitySelection.
   */
  allowCharitySelection?: boolean;
}

@Component({
  selector: 'app-language-selection-dialog',
  standalone: false,
  templateUrl: './language-selection-dialog.component.html',
  styleUrls: ['./language-selection-dialog.component.scss'],
})
export class LanguageSelectionDialogComponent implements OnInit {
  languages: GeneralLanguage[] = [];
  selectedLanguage: GeneralLanguage | null = null;
  isLoading = false;
  title = 'Select Language';
  isEditMode = false;
  entityType: EntityType = 'charity';

  constructor(
    public dialogRef: MatDialogRef<LanguageSelectionDialogComponent>,
    private generalService: GeneralService,
    private dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: LanguageSelectionDialogData
  ) {
    // If languages are provided via data, use them (edit mode)
    if (data?.languages) {
      this.languages = data.languages;
      this.isEditMode = data.isEditMode ?? true;
    }
    if (data?.title) {
      this.title = data.title;
    }
    if (data?.entityType) {
      this.entityType = data.entityType;
    }
  }

  ngOnInit(): void {
    // Only load languages if not provided via data
    if (this.languages.length === 0) {
      this.loadLanguages();
    }
  }

  /**
   * Load live languages from general service
   */
  loadLanguages(): void {
    this.isLoading = true;
    this.generalService.getGeneralDropdowns().subscribe({
      next: (response) => {
        // Filter only LIVE languages
        this.languages = response.response.languages.filter(
          (lang) => lang.state === 'LIVE'
        );
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Failed to load languages:', error);
        this.isLoading = false;
      }
    });
  }

  /**
   * Select a language
   */
  onLanguageSelect(language: GeneralLanguage): void {
    this.selectedLanguage = language;
  }

  /**
   * Close dialog without selection
   */
  onCancel(): void {
    this.dialogRef.close(null);
  }

  /**
   * Confirm language selection
   * For edit mode: returns the selected language to parent
   * For add mode: opens charity form directly
   */
  onConfirm(): void {
    if (this.selectedLanguage) {
      if (this.isEditMode) {
        // Edit mode - return selected language to parent component
        this.dialogRef.close(this.selectedLanguage);
      } else {
        // Add mode - close this dialog and open the appropriate entity form
        this.dialogRef.close();

        if (this.entityType === 'project') {
          this.dialog.open(ProjectFormComponent, {
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: '95vh',
            disableClose: false,
            panelClass: 'project-form-dialog',
            data: {
              language: this.selectedLanguage,
              allowCharitySelection: this.data?.allowCharitySelection
            }
          });
        } else {
          this.dialog.open(CharityFormComponent, {
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: '95vh',
            disableClose: false,
            panelClass: 'charity-form-dialog',
            data: {
              language: this.selectedLanguage
            }
          });
        }
      }
    }
  }

  get translationNs(): string {
    return this.entityType === 'project'
      ? 'settings.project_details_form'
      : 'settings.charity_details_form';
  }

  /**
   * Check if a language is selected
   */
  isSelected(language: GeneralLanguage): boolean {
    return this.selectedLanguage?.uuid === language.uuid;
  }
}
